import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { DollarSign, FileText, RefreshCw, TrendingUp, CheckCircle, XCircle, Plus, ShieldCheck, Download, Zap, Link2, AlertTriangle, FilePlus, Eye, X, User, Clock } from 'lucide-react';
import {
  API_BASE_URL, getAuthHeaders,
  getCreditMemos, getCreditMemoDetail, createCreditMemoFromClaim, issueCreditMemo, approveCreditMemo, rejectCreditMemo,
  applyCreditMemo, reverseCreditMemo, voidCreditMemo, downloadCreditMemo, exportCreditMemos, getGrossToNet, exportGlPostings,
  simulateRemittance, getDeductions, autoMatchDeductions, disputeDeduction, writeoffDeduction, exportArReconciliation
} from '../../config/api';

import PlatformInvoices from './PlatformInvoices';
import PillTabs from '../../components/ui/PillTabs';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_BADGE = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  REJECTED: 'bg-red-100 text-red-700',
  ISSUED: 'bg-blue-100 text-blue-800',
  APPLIED: 'bg-green-100 text-green-800',
  VOID: 'bg-gray-100 text-gray-500'
};
const STATUS_LABEL = { PENDING_APPROVAL: 'Pending Approval' };
const statusLabel = (s) => STATUS_LABEL[s] || s;

const Finance = () => {
  const [tab, setTab] = useState('memos');
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Finance & Accounts Receivable</h1>
        <p className="text-gray-600 mt-2">Issue credit memos against validated claims, reconcile wholesaler deductions (A/R), and forecast chargeback liability (gross-to-net).</p>
      </div>
      <div className="mb-6">
        <PillTabs
          active={tab}
          onChange={setTab}
          tabs={[
            { id: 'memos', label: 'Credit Memos' },
            { id: 'invoices', label: 'Platform Invoices' },
            { id: 'ar', label: 'A/R Reconciliation' },
            { id: 'gtn', label: 'Gross-to-Net Forecast' }
          ]}
        />
      </div>
      {tab === 'memos' ? <CreditMemos /> : tab === 'invoices' ? <PlatformInvoices /> : tab === 'ar' ? <ARReconciliation /> : <GrossToNet />}
    </div>
  );
};

const CreditMemos = () => {
  const [memos, setMemos] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [eligibleClaims, setEligibleClaims] = useState([]);
  const [selectedClaim, setSelectedClaim] = useState('');
  const [threshold, setThreshold] = useState(0);
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (m) => {
    setDetailLoading(true);
    setDetail({ memo: m, activity: [] }); // show immediately, enrich on load
    try { setDetail(await getCreditMemoDetail(m.id)); }
    catch (e) { toast.error('Failed to load memo detail'); }
    finally { setDetailLoading(false); }
  };
  const afterAction = async (memoId) => {
    await load();
    if (detail?.memo?.id === memoId) {
      try { setDetail(await getCreditMemoDetail(memoId)); } catch (e) { /* keep prior */ }
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const data = await getCreditMemos(params);
      setMemos(data.memos || []);
      setTotals(data.totals || {});
      if (data.approvalThreshold != null) setThreshold(data.approvalThreshold);
      // Eligible claims: validated/approved/posted, for generating memos.
      const r = await axios.get(`${API_BASE_URL}/api/chargebacks?limit=100`, { headers: getAuthHeaders() });
      const claims = r.data?.data?.claims || r.data?.data?.chargebacks || r.data?.claims || r.data?.data || [];
      setEligibleClaims(claims.filter(c => ['VALIDATED', 'APPROVED', 'POSTED'].includes(c.status) && !c.hasCreditMemo));
    } catch (e) {
      toast.error('Failed to load credit memos');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const generate = async () => {
    if (!selectedClaim) { toast.error('Select a validated claim'); return; }
    setBusy('generate');
    try { await createCreditMemoFromClaim(selectedClaim); toast.success('Credit memo generated'); setSelectedClaim(''); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to generate credit memo'); }
    finally { setBusy(null); }
  };
  const doIssue = async (m) => {
    setBusy(m.id);
    try {
      const res = await issueCreditMemo(m.id);
      if (res?.requiresApproval) toast.success(`Memo ${m.memoNumber} (${money(m.amount)}) sent for approval — over the ${money(res.threshold)} threshold`);
      else toast.success(`Memo ${m.memoNumber} issued`);
      await afterAction(m.id);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to issue'); }
    finally { setBusy(null); }
  };
  const doApprove = async (m) => {
    setBusy(m.id);
    try { await approveCreditMemo(m.id); toast.success(`Memo ${m.memoNumber} approved & issued`); await afterAction(m.id); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to approve'); }
    finally { setBusy(null); }
  };
  const doReject = async (m) => {
    const reason = window.prompt(`Reject memo ${m.memoNumber} — reason?`, '');
    if (reason === null) return;
    setBusy(m.id);
    try { await rejectCreditMemo(m.id, reason); toast.success(`Memo ${m.memoNumber} rejected`); await afterAction(m.id); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to reject'); }
    finally { setBusy(null); }
  };
  const doApply = async (m) => {
    setBusy(m.id);
    try { await applyCreditMemo(m.id); toast.success(`Memo ${m.memoNumber} applied to A/R`); await afterAction(m.id); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to apply'); }
    finally { setBusy(null); }
  };
  const doReverse = async (m) => {
    const reason = window.prompt(`Reverse ${m.memoNumber} with an offsetting debit memo — reason?`, '');
    if (reason === null) return;
    setBusy(m.id);
    try { const d = await reverseCreditMemo(m.id, reason); toast.success(`Reversed — created debit memo ${d?.memoNumber || ''}`); await afterAction(m.id); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to reverse'); }
    finally { setBusy(null); }
  };
  const doVoid = async (m) => {
    if (!window.confirm(`Void ${m.memoType === 'DEBIT' ? 'debit' : 'credit'} memo ${m.memoNumber}?`)) return;
    setBusy(m.id);
    try { await voidCreditMemo(m.id); toast.success(`Memo ${m.memoNumber} voided`); await afterAction(m.id); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to void'); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <SummaryCard icon={FileText} label="Memos" value={totals.count ?? 0} />
        <SummaryCard icon={DollarSign} label="Net Value (credits − debits)" value={money(totals.amount)} />
        <SummaryCard icon={CheckCircle} label="Issued / Applied" value={money(totals.issuedAmount)} />
        <SummaryCard icon={ShieldCheck} label="Pending Approval" value={totals.pendingApproval ?? 0} tone={totals.pendingApproval ? 'amber' : undefined} />
      </div>

      {/* Generate from claim */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Issue a credit memo from a validated claim</h3>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <select value={selectedClaim} onChange={e => setSelectedClaim(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="">Select a validated / approved / posted claim…</option>
              {eligibleClaims.map(c => (
                <option key={c.id} value={c.id}>{c.claimNumber} — {money(c.postedAmount || c.totalAmount)} ({c.status})</option>
              ))}
            </select>
          </div>
          <button disabled={busy === 'generate' || !selectedClaim} onClick={generate} title="Generate Credit Memo"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">
            <Plus className="h-4 w-4 mr-2" /> Generate Credit Memo
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Lifecycle: Draft → Issue → Applied. Memos of {money(threshold)} or more require a second approver before they can be issued (segregation of duties). Issued/applied credits can be reversed with an offsetting debit memo.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Credit Memos</h3>
          <div className="flex items-center gap-2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-md px-2 py-2 text-sm">
              <option value="">All statuses</option>
              {['DRAFT', 'PENDING_APPROVAL', 'ISSUED', 'APPLIED', 'REJECTED', 'VOID'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
            <button onClick={() => exportCreditMemos(statusFilter ? { status: statusFilter } : {})} title="Export all as CSV" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </button>
            <button onClick={load} title="Refresh" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr>
              {['Memo #', 'Type', 'Claim', 'Customer', 'Amount', 'Status', 'Issued', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : memos.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                  <FileText className="h-8 w-8 mx-auto text-gray-300 mb-2" />No credit memos yet. Generate one from a validated claim above.
                </td></tr>
              ) : memos.map(m => {
                const isDebit = m.memoType === 'DEBIT';
                return (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {m.memoNumber}
                    {m.reverses?.memoNumber && <div className="text-xs text-gray-400 font-normal">reverses {m.reverses.memoNumber}</div>}
                    {m.reversedBy?.length > 0 && <div className="text-xs text-red-500 font-normal">reversed by {m.reversedBy.map(r => r.memoNumber).join(', ')}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${isDebit ? 'bg-orange-100 text-orange-800' : 'bg-blue-50 text-blue-700'}`}>{isDebit ? 'Debit' : 'Credit'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{m.claim?.claimNumber || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{m.customerName || '—'}</td>
                  <td className={`px-4 py-3 text-sm ${isDebit ? 'text-orange-700' : 'text-gray-900'}`}>{isDebit ? '−' : ''}{money(m.amount)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[m.status] || 'bg-gray-100 text-gray-700'}`}>{statusLabel(m.status)}</span>
                    {m.status === 'REJECTED' && m.rejectionReason && <div className="text-xs text-gray-400 mt-0.5">{m.rejectionReason}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{m.issuedAt ? new Date(m.issuedAt).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap space-x-3">
                    <button onClick={() => openDetail(m)} className="text-gray-500 hover:text-gray-800 inline-flex items-center" title="View details"><Eye className="h-4 w-4" /></button>
                    <button onClick={() => downloadCreditMemo(m.id)} className="text-gray-500 hover:text-gray-800 inline-flex items-center" title="Download CSV"><Download className="h-4 w-4" /></button>
                    {(m.status === 'DRAFT' || m.status === 'REJECTED') && (
                      <button disabled={busy === m.id} onClick={() => doIssue(m)} className="text-primary-600 hover:text-primary-800" title="Issue">Issue</button>
                    )}
                    {m.status === 'PENDING_APPROVAL' && (
                      <>
                        <button disabled={busy === m.id} onClick={() => doApprove(m)} className="text-green-600 hover:text-green-800" title="Approve">Approve</button>
                        <button disabled={busy === m.id} onClick={() => doReject(m)} className="text-red-500 hover:text-red-700" title="Reject">Reject</button>
                      </>
                    )}
                    {m.status === 'ISSUED' && (
                      <button disabled={busy === m.id} onClick={() => doApply(m)} className="text-green-600 hover:text-green-800" title="Apply">Apply</button>
                    )}
                    {!isDebit && (m.status === 'ISSUED' || m.status === 'APPLIED') && !(m.reversedBy?.length) && (
                      <button disabled={busy === m.id} onClick={() => doReverse(m)} className="text-orange-600 hover:text-orange-800" title="Reverse">Reverse</button>
                    )}
                    {m.status !== 'VOID' && (
                      <button disabled={busy === m.id} onClick={() => doVoid(m)} className="text-gray-400 hover:text-gray-600" title="Void">Void</button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <CreditMemoDetailModal
          detail={detail}
          loading={detailLoading}
          busy={busy}
          onClose={() => setDetail(null)}
          actions={{ doIssue, doApprove, doReject, doApply, doReverse, doVoid }}
        />
      )}
    </div>
  );
};

// Full credit-memo detail for review before acting: memo + underlying claim
// (lines, contract, batch) + audit trail, with contextual action buttons.
const CreditMemoDetailModal = ({ detail, loading, busy, onClose, actions }) => {
  const m = detail.memo || {};
  const claim = m.claim;
  const isDebit = m.memoType === 'DEBIT';
  const Field = ({ label, children }) => (
    <div><dt className="text-xs text-gray-500">{label}</dt><dd className="text-sm text-gray-900">{children || '—'}</dd></div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[88vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary-600" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">{m.memoNumber}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${isDebit ? 'bg-orange-100 text-orange-800' : 'bg-blue-50 text-blue-700'}`}>{isDebit ? 'Debit' : 'Credit'}</span>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[m.status] || 'bg-gray-100 text-gray-700'}`}>{statusLabel(m.status)}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-6">
          {/* Memo summary */}
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
            <Field label="Amount"><span className={isDebit ? 'text-orange-700 font-medium' : 'font-medium'}>{isDebit ? '−' : ''}{money(m.amount)}</span></Field>
            <Field label="Customer">{m.customer?.name || m.customerName}</Field>
            <Field label="Claim">{claim?.claimNumber}</Field>
            <Field label="Created by">{m.createdByName}</Field>
            <Field label="Issued">{m.issuedAt ? `${m.issuedByName || ''} · ${new Date(m.issuedAt).toLocaleString()}` : '—'}</Field>
            <Field label="Approved">{m.approvedAt ? `${m.approvedByName || ''} · ${new Date(m.approvedAt).toLocaleString()}` : '—'}</Field>
            {m.reverses?.memoNumber && <Field label="Reverses">{m.reverses.memoNumber}</Field>}
            {m.reversedBy?.length > 0 && <Field label="Reversed by">{m.reversedBy.map(r => r.memoNumber).join(', ')}</Field>}
            {m.rejectionReason && <Field label="Rejection reason">{m.rejectionReason}</Field>}
          </dl>
          {m.notes && <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">{m.notes}</div>}

          {/* Underlying claim */}
          {claim && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Underlying claim</h4>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 mb-3">
                <Field label="Status">{claim.status}</Field>
                <Field label="Contract">{claim.contract?.number}</Field>
                <Field label="Batch">{claim.batch?.batchNumber}</Field>
                <Field label="Total">{money(claim.totalAmount)}</Field>
              </dl>
              <div className="overflow-x-auto border border-gray-100 rounded">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50"><tr>
                    {['#', 'Product', 'Qty', 'Sales Price', 'Contract Price', 'Variance', 'Status'].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500">{h}</th>)}
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {(claim.lines || []).map(l => (
                      <tr key={l.id}>
                        <td className="px-3 py-2 text-gray-500">{l.lineNo}</td>
                        <td className="px-3 py-2 text-gray-900">{l.product?.name || l.productNdc}<div className="text-xs text-gray-400">{l.product?.ndc11 || l.productNdc}</div></td>
                        <td className="px-3 py-2 text-gray-700">{l.quantity}</td>
                        <td className="px-3 py-2 text-gray-700">{money(l.salesPrice)}</td>
                        <td className="px-3 py-2 text-gray-700">{money(l.contractPrice)}</td>
                        <td className="px-3 py-2 text-gray-700">{l.variance != null ? money(l.variance) : '—'}</td>
                        <td className="px-3 py-2"><span className="text-xs text-gray-600">{l.validationStatus}</span></td>
                      </tr>
                    ))}
                    {(!claim.lines || claim.lines.length === 0) && <tr><td colSpan={7} className="px-3 py-3 text-center text-gray-400">No line items</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audit trail */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><Clock className="h-4 w-4 text-gray-500" /> Activity</h4>
            {loading ? <p className="text-sm text-gray-400">Loading…</p> : (detail.activity || []).length === 0 ? (
              <p className="text-sm text-gray-400">No recorded activity yet.</p>
            ) : (
              <ol className="relative border-l border-gray-200 ml-2 space-y-3">
                {detail.activity.map((a, i) => (
                  <li key={i} className="ml-4">
                    <div className="absolute -left-1.5 mt-1.5 h-2.5 w-2.5 rounded-full bg-primary-500 border border-white" />
                    <div className="text-sm text-gray-900">{(a.action || '').replace(/_/g, ' ')}</div>
                    <div className="flex items-center gap-1 text-xs text-gray-500"><User className="h-3 w-3" />{a.by}<span>·</span>{a.at ? new Date(a.at).toLocaleString() : ''}</div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Contextual actions */}
        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-3 flex-wrap">
          {(m.status === 'DRAFT' || m.status === 'REJECTED') && <button disabled={busy === m.id} onClick={() => actions.doIssue(m)} className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50" title="Issue">Issue</button>}
          {m.status === 'PENDING_APPROVAL' && <>
            <button disabled={busy === m.id} onClick={() => actions.doReject(m)} className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50" title="Reject">Reject</button>
            <button disabled={busy === m.id} onClick={() => actions.doApprove(m)} className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50" title="Approve">Approve</button>
          </>}
          {m.status === 'ISSUED' && <button disabled={busy === m.id} onClick={() => actions.doApply(m)} className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50" title="Apply">Apply</button>}
          {!isDebit && (m.status === 'ISSUED' || m.status === 'APPLIED') && !(m.reversedBy?.length) && <button disabled={busy === m.id} onClick={() => actions.doReverse(m)} className="px-3 py-2 text-sm font-medium text-orange-700 border border-orange-200 rounded-md hover:bg-orange-50 disabled:opacity-50" title="Reverse">Reverse</button>}
          {m.status !== 'VOID' && <button disabled={busy === m.id} onClick={() => actions.doVoid(m)} className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50" title="Void">Void</button>}
          <button onClick={onClose} className="px-3 py-2 text-sm font-medium text-gray-700" title="Close">Close</button>
        </div>
      </div>
    </div>
  );
};

const GrossToNet = () => {
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postFrom, setPostFrom] = useState('');
  const [postTo, setPostTo] = useState('');
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await getGrossToNet(months)); }
    catch (e) { toast.error('Failed to load gross-to-net forecast'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [months]);

  const exportPostings = async () => {
    setPosting(true);
    try { await exportGlPostings({ from: postFrom || undefined, to: postTo || undefined }); toast.success('GL & GTN postings downloaded'); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to export postings'); }
    finally { setPosting(false); }
  };

  if (loading || !data) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">Loading forecast…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-700">Forecast horizon</label>
        <select value={months} onChange={e => setMonths(Number(e.target.value))} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm">
          {[1, 3, 6, 12].map(m => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
        </select>
        <button onClick={load} title="Refresh" className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={DollarSign} label="Posted (actual expense)" value={money(data.postedActual)} />
        <SummaryCard icon={FileText} label="Open liability (pipeline)" value={money(data.openLiability)} tone="amber" />
        <SummaryCard icon={TrendingUp} label={`Projected (${data.forecastMonths} mo)`} value={money(data.projectedExpense)} />
        <SummaryCard icon={CheckCircle} label="Recommended reserve" value={money(data.recommendedReserve)} tone="primary" />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">How this reserve is estimated</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row label="Monthly run-rate (posted / observed months)" value={money(data.monthlyRunRate)} />
          <Row label="Observed months of history" value={data.observedMonths} />
          <Row label="Open claims liability (accrued, not posted)" value={money(data.openLiability)} />
          <Row label={`Projected new chargebacks (${data.forecastMonths} mo × run-rate)`} value={money(data.projectedExpense)} />
          <Row label="Rejected claims (excluded)" value={money(data.rejected)} />
          <Row label="Credit memos issued/applied" value={money(data.creditMemosIssued)} />
        </dl>
        <p className="mt-4 text-xs text-gray-500">
          Recommended reserve = open liability + projected new chargebacks over the horizon. Run-rate is the average monthly posted chargeback expense across the observed history. Validate against channel-inventory data before booking reserves.
        </p>
        <p className="mt-1 text-xs text-gray-400">As of {new Date(data.asOf).toLocaleString()} · {data.claimCount} claims analyzed</p>
      </div>

      {/* GL & GTN Postings export */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">GL &amp; GTN Postings File</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Double-entry journal file for the General Ledger / ERP: accrual builds the chargeback reserve (DR expense / CR reserve), settlement of credit memos draws it down against A/R, and reversals (debit memos) put it back. The batch is balanced (total debits = total credits).
        </p>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period — From</label>
            <input type="date" value={postFrom} max={postTo || undefined} onChange={e => setPostFrom(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period — To</label>
            <input type="date" value={postTo} min={postFrom || undefined} onChange={e => setPostTo(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <button disabled={posting} onClick={exportPostings} title="Export Postings (CSV)"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">
            <Download className="h-4 w-4 mr-2" /> {posting ? 'Exporting…' : 'Export Postings (CSV)'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">Leave dates blank to export all periods. GL accounts (expense 5010 / reserve 2410 / A/R 1210) and cost center are configurable in company settings.</p>
      </div>
    </div>
  );
};

const DED_BADGE = {
  OPEN: 'bg-amber-100 text-amber-800',
  MATCHED: 'bg-green-100 text-green-800',
  DISPUTED: 'bg-red-100 text-red-700',
  WRITTEN_OFF: 'bg-gray-100 text-gray-500'
};

const ARReconciliation = () => {
  const [deductions, setDeductions] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getDeductions(statusFilter ? { status: statusFilter } : {});
      setDeductions(data.deductions || []);
      setTotals(data.totals || {});
    } catch (e) { toast.error('Failed to load deductions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const doSimulate = async () => {
    setBusy('sim');
    try { const r = await simulateRemittance(); toast.success(`Imported remittance ${r.remittanceNumber} with ${r.deductionCount} deductions`); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to simulate remittance'); }
    finally { setBusy(null); }
  };
  const doAutoMatch = async () => {
    setBusy('match');
    try { const r = await autoMatchDeductions(); toast.success(`Matched ${r.matched} of ${r.scanned} open deductions`); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Auto-match failed'); }
    finally { setBusy(null); }
  };
  const doDispute = async (d) => {
    const reason = window.prompt(`Dispute deduction ${d.claimReference || d.id} — reason?`, 'Unauthorized / no matching claim');
    if (reason === null) return;
    setBusy(d.id);
    try { await disputeDeduction(d.id, reason); toast.success('Deduction disputed'); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to dispute'); }
    finally { setBusy(null); }
  };
  const doWriteoff = async (d) => {
    if (!window.confirm(`Write off deduction ${d.claimReference || d.id} (${money(d.amount)})?`)) return;
    setBusy(d.id);
    try { await writeoffDeduction(d.id); toast.success('Deduction written off'); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to write off'); }
    finally { setBusy(null); }
  };
  const doExport = async () => {
    setBusy('export');
    try { await exportArReconciliation(); toast.success('Reconciliation file downloaded'); }
    catch (e) { toast.error('Failed to export'); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <SummaryCard icon={FileText} label="Deductions" value={totals.count ?? 0} />
        <SummaryCard icon={AlertTriangle} label="Open (unreconciled)" value={money(totals.openAmount)} tone={totals.OPEN ? 'amber' : undefined} />
        <SummaryCard icon={CheckCircle} label="Matched" value={totals.MATCHED ?? 0} />
        <SummaryCard icon={XCircle} label="Disputed" value={totals.DISPUTED ?? 0} tone={totals.DISPUTED ? 'amber' : undefined} />
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <p className="text-sm text-gray-600 mb-3">
          Wholesalers short-pay (deduct) chargebacks from their remittances before your credit is applied. Import a remittance, auto-match its deductions to issued credit memos, dispute unauthorized ones, and export the reconciliation.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button disabled={busy === 'sim'} onClick={doSimulate} title="Simulate Remittance" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
            <FilePlus className="h-4 w-4 mr-2" /> Simulate Remittance
          </button>
          <button disabled={busy === 'match'} onClick={doAutoMatch} title="Auto-match Deductions" className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">
            <Zap className="h-4 w-4 mr-2" /> Auto-match Deductions
          </button>
          <button disabled={busy === 'export'} onClick={doExport} title="Export Reconciliation (CSV)" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
            <Download className="h-4 w-4 mr-2" /> Export Reconciliation (CSV)
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Deductions</h3>
          <div className="flex items-center gap-2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-md px-2 py-2 text-sm">
              <option value="">All statuses</option>
              {['OPEN', 'MATCHED', 'DISPUTED', 'WRITTEN_OFF'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <button onClick={() => exportCreditMemos(statusFilter ? { status: statusFilter } : {})} title="Export all as CSV" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </button>
            <button onClick={load} title="Refresh" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr>
              {['Remittance', 'Claim Ref', 'Customer', 'Deduction', 'Status', 'Matched Memo', 'Variance', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : deductions.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                  <FileText className="h-8 w-8 mx-auto text-gray-300 mb-2" />No deductions yet. Click "Simulate Remittance" to load a wholesaler remittance and reconcile it.
                </td></tr>
              ) : deductions.map(d => {
                const v = d.variance != null ? Number(d.variance) : null;
                return (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{d.remittance?.remittanceNumber || '—'}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">{d.claimReference || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{d.customerName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{money(d.amount)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${DED_BADGE[d.status] || 'bg-gray-100 text-gray-700'}`}>{d.status.replace('_', ' ')}</span>
                    {d.status === 'DISPUTED' && d.disputeReason && <div className="text-xs text-gray-400 mt-0.5">{d.disputeReason}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{d.creditMemo?.memoNumber ? <span className="inline-flex items-center"><Link2 className="h-3 w-3 mr-1 text-green-500" />{d.creditMemo.memoNumber}</span> : '—'}</td>
                  <td className={`px-4 py-3 text-sm ${v ? 'text-red-600' : 'text-gray-500'}`}>{v == null ? '—' : (v > 0 ? '+' : '') + money(v)}</td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap space-x-3">
                    {d.status === 'OPEN' && (
                      <button disabled={busy === d.id} onClick={() => doDispute(d)} className="text-red-500 hover:text-red-700" title="Dispute">Dispute</button>
                    )}
                    {(d.status === 'OPEN' || d.status === 'DISPUTED') && (
                      <button disabled={busy === d.id} onClick={() => doWriteoff(d)} className="text-gray-400 hover:text-gray-600" title="Write off">Write off</button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, tone }) => {
  const toneCls = tone === 'amber' ? 'text-amber-600' : tone === 'primary' ? 'text-primary-600' : 'text-gray-500';
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-center">
        <Icon className={`h-7 w-7 ${toneCls}`} />
        <div className="ml-3">
          <p className="text-xs font-medium text-gray-500">{label}</p>
          <p className="text-xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between border-b border-gray-100 pb-2">
    <dt className="text-gray-600">{label}</dt>
    <dd className="text-gray-900 font-medium">{value}</dd>
  </div>
);

export default Finance;
